import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Droplet, CircleDot, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/lib/api';
// The brand list lives with the tyre table; one list, one place.
import { TIRE_BRANDS } from '@/pages/maintenance/ConsumablesPanels';

/**
 * Oil and tyres, picked while the job is being logged.
 *
 * This is the moment the data has to be captured. A mechanic who has just
 * drained a sump will not go to a stock screen afterwards to say so, which is
 * why the drums stayed full and the parts table stayed empty. Asking here costs
 * two taps and makes the truck's bill true.
 *
 * Shared by the extraordinary inspection and the Quick Log so both charge a job
 * the same way — one of them behaving differently is how two sets of numbers
 * start to disagree.
 */

export interface ConsumableSelection {
  drumId: string;
  liters: string;
  tireIds: string[];
}

export const EMPTY_CONSUMABLES: ConsumableSelection = { drumId: '', liters: '', tireIds: [] };

interface Drum {
  id: string; label: string; grade: string;
  remaining_liters: string; cost_per_liter_sar: string; is_active: boolean;
}
interface Tire {
  id: string; serial_number: string; brand: string; size_model: string | null;
  unit_cost_sar: string | null; status: string;
}

/**
 * Write the selection onto a job. Returns what could not be applied, so the
 * caller can say so rather than pretending it all worked: the inspection itself
 * is already saved by this point and must not be lost to a drum being short.
 */
export async function commitConsumables(
  eventId: string, vehicleId: string, sel: ConsumableSelection,
): Promise<string[]> {
  const problems: string[] = [];
  const reason = (e: unknown, fallback: string) =>
    (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;

  if (sel.drumId && Number(sel.liters) > 0) {
    try {
      await apiClient.post(`/api/v1/consumables/oil-drums/${sel.drumId}/draw`, {
        liters: Number(sel.liters), maintenanceEventId: eventId,
      });
    } catch (e) {
      problems.push(reason(e, 'The oil could not be drawn from that drum.'));
    }
  }
  // One at a time, so one bad serial does not take the others down with it.
  for (const tireId of sel.tireIds) {
    try {
      await apiClient.post(`/api/v1/consumables/tires/${tireId}/mount`, {
        vehicleId, maintenanceEventId: eventId,
      });
    } catch (e) {
      problems.push(reason(e, 'A tyre could not be mounted.'));
    }
  }
  return problems;
}

export function ConsumablesPicker({
  value, onChange, enabled = true, compact = false,
}: {
  value: ConsumableSelection;
  onChange: (next: ConsumableSelection) => void;
  enabled?: boolean;
  compact?: boolean;
}) {
  const qc = useQueryClient();
  /**
   * Opening a drum or unwrapping a tyre happens at the truck, not at a stock
   * screen. Recording it here keeps the mechanic in the job they are logging;
   * sending them away to Spare Parts is how it stops being recorded at all.
   */
  const [adding, setAdding] = useState<null | 'drum' | 'tire'>(null);
  const [busy, setBusy] = useState(false);
  const [addErr, setAddErr] = useState<string | null>(null);
  const [f, setF] = useState({ label: '', grade: '15W40', cap: '208', price: '', serial: '', brand: TIRE_BRANDS[0], size: '', cost: '' });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));

  const createDrum = async () => {
    if (!f.label.trim() || !(Number(f.cap) > 0)) { setAddErr('A label and a capacity are needed.'); return; }
    setBusy(true); setAddErr(null);
    try {
      const r = await apiClient.post<{ id: string }>('/api/v1/consumables/oil-drums', {
        label: f.label.trim(), grade: f.grade.trim() || 'unspecified',
        capacityLiters: Number(f.cap), purchasePriceSar: Number(f.price) || 0,
      });
      await qc.invalidateQueries({ queryKey: ['oil-drums'] });
      // Selected straight away: it was added because it is about to be used.
      onChange({ ...value, drumId: r.data.id });
      setAdding(null); setF((x) => ({ ...x, label: '', price: '' }));
    } catch (e) {
      setAddErr((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'The drum could not be added.');
    } finally { setBusy(false); }
  };

  const createTire = async () => {
    if (!f.serial.trim()) { setAddErr('A tyre needs its serial number.'); return; }
    setBusy(true); setAddErr(null);
    try {
      const r = await apiClient.post<{ id: string }>('/api/v1/consumables/tires', {
        serialNumber: f.serial.trim(), brand: f.brand,
        ...(f.size.trim() && { sizeModel: f.size.trim() }),
        ...(Number(f.cost) > 0 && { unitCostSar: Number(f.cost) }),
      });
      await qc.invalidateQueries({ queryKey: ['tires'] });
      onChange({ ...value, tireIds: [...value.tireIds, r.data.id] });
      setAdding(null); setF((x) => ({ ...x, serial: '', size: '', cost: '' }));
    } catch (e) {
      setAddErr((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'The tyre could not be added.');
    } finally { setBusy(false); }
  };

  const { data: drums } = useQuery({
    queryKey: ['oil-drums'],
    enabled,
    queryFn: async () => (await apiClient.get<{ items: Drum[] }>('/api/v1/consumables/oil-drums')).data.items,
  });
  const { data: tires } = useQuery({
    queryKey: ['tires', 'in_stock'],
    enabled,
    queryFn: async () => (await apiClient.get<{ items: Tire[] }>('/api/v1/consumables/tires',
      { params: { status: 'in_stock' } })).data.items,
  });

  const usable = (drums ?? []).filter((d) => d.is_active && Number(d.remaining_liters) > 0);
  const drum = usable.find((d) => d.id === value.drumId);
  const litres = Number(value.liters);
  const over = drum != null && litres > Number(drum.remaining_liters);
  const oilCost = drum && litres > 0 ? litres * Number(drum.cost_per_liter_sar) : 0;
  const stock = tires ?? [];

  // Nothing to offer is not an error; it is a set-up step, said once.


  return (
    <div className={compact ? 'space-y-3' : 'space-y-3 sm:col-span-2'}>
      <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Droplet className="h-3.5 w-3.5" /> Oil used (optional)
            <button type="button" onClick={() => { setAdding(adding === 'drum' ? null : 'drum'); setAddErr(null); }}
              className="ms-auto inline-flex items-center gap-1 hover:text-foreground transition-colors">
              <Plus className="h-3 w-3" /> New drum
            </button>
          </div>

          {adding === 'drum' && (
            <div className="rounded-md border border-border p-2 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input value={f.label} onChange={(e) => set('label', e.target.value)} placeholder="Drum #01" aria-label="Drum label" />
                <Input value={f.grade} onChange={(e) => set('grade', e.target.value)} placeholder="15W40" aria-label="Grade" />
                <Input inputMode="decimal" value={f.cap} onChange={(e) => set('cap', e.target.value)} placeholder="Litres" aria-label="Capacity litres" />
                <Input inputMode="decimal" value={f.price} onChange={(e) => set('price', e.target.value)} placeholder="Price SAR" aria-label="Purchase price" />
              </div>
              {Number(f.cap) > 0 && Number(f.price) > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {(Number(f.price) / Number(f.cap)).toFixed(4)} SAR per litre.
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setAdding(null)}>Cancel</Button>
                <Button size="sm" variant="gold" className="flex-1" disabled={busy} onClick={createDrum}>
                  {busy ? 'Adding…' : 'Add drum'}
                </Button>
              </div>
            </div>
          )}

          {usable.length === 0 && adding !== 'drum' && (
            <p className="text-[11px] text-muted-foreground">No drum with oil left — add one above.</p>
          )}
          {usable.length > 0 && (
          <>
          <div className="grid grid-cols-2 gap-2">
            <select value={value.drumId}
              onChange={(e) => onChange({ ...value, drumId: e.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              <option value="">— pick a drum —</option>
              {usable.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label} · {d.grade} ({Number(d.remaining_liters).toFixed(0)} L left)
                </option>
              ))}
            </select>
            <Input inputMode="decimal" value={value.liters} placeholder="Litres used"
              aria-label="Litres used"
              onChange={(e) => onChange({ ...value, liters: e.target.value })} />
          </div>
          {drum && litres > 0 && (
            <p className={`text-[11px] ${over ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
              {over
                ? `Only ${Number(drum.remaining_liters).toFixed(0)} L left in ${drum.label}.`
                : `${litres} L × ${Number(drum.cost_per_liter_sar).toFixed(4)} = ${oilCost.toFixed(2)} SAR charged to this job.`}
            </p>
          )}
          </>
          )}
      </div>

      <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground flex items-center gap-1.5">
            <CircleDot className="h-3.5 w-3.5" /> Tyres fitted (optional)
            <button type="button" onClick={() => { setAdding(adding === 'tire' ? null : 'tire'); setAddErr(null); }}
              className="ms-auto inline-flex items-center gap-1 hover:text-foreground transition-colors">
              <Plus className="h-3 w-3" /> New tyre
            </button>
          </div>

          {adding === 'tire' && (
            <div className="rounded-md border border-border p-2 space-y-2">
              <Input value={f.serial} onChange={(e) => set('serial', e.target.value)} placeholder="Serial number" aria-label="Serial number" />
              <div className="grid grid-cols-2 gap-2">
                <select value={f.brand} onChange={(e) => set('brand', e.target.value)}
                  className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
                  {TIRE_BRANDS.map((b: string) => <option key={b} value={b}>{b}</option>)}
                </select>
                <Input value={f.size} onChange={(e) => set('size', e.target.value)} placeholder="315/80R22.5" aria-label="Size" />
              </div>
              <Input inputMode="decimal" value={f.cost} onChange={(e) => set('cost', e.target.value)} placeholder="Cost SAR" aria-label="Unit cost" />
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setAdding(null)}>Cancel</Button>
                <Button size="sm" variant="gold" className="flex-1" disabled={busy} onClick={createTire}>
                  {busy ? 'Adding…' : 'Add tyre'}
                </Button>
              </div>
            </div>
          )}

          {stock.length === 0 && adding !== 'tire' && (
            <p className="text-[11px] text-muted-foreground">No tyres in stock — add one above.</p>
          )}
          {stock.length > 0 && (
          <>
          <div className="rounded-md border border-border max-h-36 overflow-y-auto divide-y divide-border">
            {stock.map((t) => {
              const on = value.tireIds.includes(t.id);
              return (
                <button key={t.id} type="button"
                  onClick={() => onChange({
                    ...value,
                    tireIds: on ? value.tireIds.filter((x) => x !== t.id) : [...value.tireIds, t.id],
                  })}
                  className={`w-full text-start px-2 py-1.5 flex items-center gap-2 transition ${
                    on ? 'bg-primary/10' : 'hover:bg-muted/50'}`}>
                  <span className={`h-3.5 w-3.5 rounded border shrink-0 ${
                    on ? 'bg-primary border-primary' : 'border-border'}`} />
                  <span className="text-sm flex-1 truncate">{t.brand} · {t.serial_number}</span>
                  <span className="text-[11px] text-muted-foreground">{t.size_model ?? ''}</span>
                  <span className="text-[11px] text-muted-foreground font-mono">
                    {t.unit_cost_sar ? `${Number(t.unit_cost_sar).toFixed(0)} SAR` : ''}
                  </span>
                </button>
              );
            })}
          </div>
          {value.tireIds.length > 0 && (
            <p className="text-[11px] text-muted-foreground">
              {value.tireIds.length} tyre(s) — marked mounted on this truck and charged to this job.
            </p>
          )}
          </>
          )}
      </div>

      {addErr && <p className="text-[11px] text-rose-600 dark:text-rose-400">{addErr}</p>}
    </div>
  );
}
